import type { ReactNode } from 'react'
import { CoachingReviewProvider } from '../store'
import {
  type UseReviewPersistenceApi,
  useReviewSlice,
  useTrpcReviewPersistenceApi,
} from './review-slice'

/**
 * Live review lane boundary (LG-503). Replaces the mock-provider's review
 * slice with one backed by `coachingV2.reviews.*` for everything rendered
 * inside it — the Review tab body, plus the tab bar it is wrapped around so
 * the review badge reads the same lane.
 *
 * Two deliberate differences from `PersistedCurriculumBoundary`:
 *
 * - It never renders *instead of* its children. The tab bar lives inside it,
 *   and blanking that while the queue loads would take the coach's way out of
 *   the tab with it; loading and error states travel on the slice's `status`
 *   and are rendered by the queue.
 * - It stays mounted while the coach is on other tabs, with `active` gating
 *   the reads instead. Unmounting it would swap the element type at the top of
 *   the shell body on every tab switch, which tears down and rebuilds the tab
 *   bar itself — dropping keyboard focus mid-navigation — and would throw away
 *   the tab's local UI state each time.
 */
export function PersistedReviewBoundary(props: {
  clientId: string
  /** Whether the Review tab is the one on screen. Gates the queries. */
  active: boolean
  useApi?: UseReviewPersistenceApi
  children: ReactNode
}): ReactNode {
  const useApi = props.useApi ?? useTrpcReviewPersistenceApi
  const api = useApi(props.clientId, { enabled: props.active })
  const review = useReviewSlice(api)

  return (
    <CoachingReviewProvider review={review}>
      {props.children}
    </CoachingReviewProvider>
  )
}
